import React from 'react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { CalendarIcon, Repeat, X } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { RecurrencePattern } from '@/types/lead';

interface RecurrenceSelectorProps {
  pattern: RecurrencePattern | null;
  interval: number;
  endDate: string | null;
  onChange: (values: { pattern: RecurrencePattern | null; interval: number; endDate: string | null }) => void;
}

const patternLabels: Record<RecurrencePattern, string> = {
  daily: 'day(s)',
  weekly: 'week(s)',
  monthly: 'month(s)',
  yearly: 'year(s)',
};

const RecurrenceSelector: React.FC<RecurrenceSelectorProps> = ({ pattern, interval, endDate, onChange }) => {
  const isEnabled = !!pattern;

  const handleToggle = (checked: boolean) => {
    if (checked) {
      onChange({ pattern: 'weekly', interval: 1, endDate: null });
    } else {
      onChange({ pattern: null, interval: 1, endDate: null });
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Switch checked={isEnabled} onCheckedChange={handleToggle} />
        <Label className="flex items-center gap-1.5 cursor-pointer font-normal">
          <Repeat className="h-4 w-4 text-muted-foreground" />
          Repeat this task
        </Label>
      </div>

      {isEnabled && (
        <div className="pl-2 space-y-3 border-l-2 border-border ml-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-muted-foreground">Every</span>
            <Input
              type="number"
              min={1}
              max={99}
              value={interval}
              onChange={(e) => onChange({ pattern, interval: Math.max(1, parseInt(e.target.value) || 1), endDate })}
              className="w-16 h-8 text-sm"
            />
            <Select
              value={pattern!}
              onValueChange={(val) => onChange({ pattern: val as RecurrencePattern, interval, endDate })}
            >
              <SelectTrigger className="w-[120px] h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">day(s)</SelectItem>
                <SelectItem value="weekly">week(s)</SelectItem>
                <SelectItem value="monthly">month(s)</SelectItem>
                <SelectItem value="yearly">year(s)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Ends</span>
            {endDate ? (
              <div className="flex items-center gap-1">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 text-sm">
                      <CalendarIcon className="mr-1.5 h-3.5 w-3.5" />
                      {format(new Date(endDate), 'PPP')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={new Date(endDate)}
                      onSelect={(date) => onChange({ pattern, interval, endDate: date ? date.toISOString() : null })}
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => onChange({ pattern, interval, endDate: null })}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 text-sm text-muted-foreground">
                    <CalendarIcon className="mr-1.5 h-3.5 w-3.5" />
                    Never (set end date)
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={undefined}
                    onSelect={(date) => onChange({ pattern, interval, endDate: date ? date.toISOString() : null })}
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RecurrenceSelector;
